import { useFragment } from '@apollo/client';

import {
  ProductUserStatus,
  ProductVisibleStatus,
} from '@models';
import { useIsAuth } from '@hook/useIsAuth';
import { useCreateProductDialog } from '@hook/useCreateProductDialog';
import { notifyError } from '@utils/notifications';
import { ProductDetailsBottomBar, FormData } from '@components/product/ProductDetailsBottomBar';
import { Skeleton } from '@ui/Skeleton';

import {
  ProductDetailsBottomBarProductFragment,
  ProductDetailsBottomBarProductFragmentDoc,
  useProductDetailsBottomBarThreadQuery,
} from './gql/graphql.v3.generated';

type Product = {
  __typename?: 'ProductDetails',
  id: number,
}

interface Props {
  className?: string,
  product?: Product,
}

export const ProductDetailsBottomBarContainer: React.FC<Props> = ({
  className,
  product,
}) => {
  const isAuth = useIsAuth();

  const { data: productData, complete: productDataComplete } = useFragment<ProductDetailsBottomBarProductFragment>({
    fragment: ProductDetailsBottomBarProductFragmentDoc,
    from: product || null,
  });

  const {
    data: threadData,
    loading: threadLoading,
  } = useProductDetailsBottomBarThreadQuery({
    variables: {
      dialogId: productData?.dialogId,
    },
    context: {
      batch: true,
    },
    skip: !productData?.dialogId,
  });

  const handleCreateDialog = useCreateProductDialog();

  const handleSubmit = async (formData: FormData) => {
    if (!formData.body || !productData.id) {
      return null;
    }

    const errors = await handleCreateDialog({
      productId: productData.id,
      body: formData.body,
    });

    if (errors?.body || errors?.__all__) {
      notifyError(errors?.body || errors?.__all__);
    }

    return null;
  };

  const canStartDialog = (
    productData?.userStatusTitle !== ProductUserStatus.Hidden
    && productData?.statusTitle === ProductVisibleStatus.Available
    && !productData.seller?.isUserRestrictedByBankId
  );

  if (!productDataComplete || threadLoading) {
    return (
      <div className={className}>
        <Skeleton
          width='100%'
          height='57px'
        />
      </div>
    );
  }

  return (
    <ProductDetailsBottomBar
      className={className}
      isAuth={!!isAuth}
      threadId={threadData?.viewer.thread?.threadId}
      product={product ? {
        id: product.id,
        name: productData.name,
        price: productData.price || 0,
        catalog: productData.catalog,
        brand: productData.brand,
        likes: productData.likes || 0,
        isLiked: !!productData.isLiked,
      } : null}
      canStartDialog={canStartDialog}
      onSendMessage={handleSubmit}
    />
  );
};
